import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Section, DataCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const ROLES = ["admin", "hr", "finance", "operasional", "karyawan"];
const MODULES = ["dashboard", "absensi", "karyawan", "sites", "assets", "hmkm", "bbm", "maintenance", "sparepart", "produksi", "payroll", "kasbon", "kas-bank", "purchasing", "inventory", "approval", "budget", "laporan", "holidays", "shifts", "rekap", "movements", "quick", "settings"];

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="p-6 text-sm text-neutral-500 font-mono">Memuat...</div>;

  const set = (k, v) => setS({ ...s, [k]: v });
  const toggleDay = (d) => {
    const days = new Set(s.work_days || []);
    if (days.has(d)) days.delete(d); else days.add(d);
    set("work_days", [...days].sort());
  };
  const togglePerm = (role, mod) => {
    const perms = { ...(s.role_permissions || {}) };
    const arr = new Set(perms[role] || []);
    if (arr.has(mod)) arr.delete(mod); else arr.add(mod);
    perms[role] = [...arr];
    set("role_permissions", perms);
  };

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Settings tersimpan");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const num = (v) => (v === "" || v == null ? null : Number(v));

  return (
    <div>
      <PageHeader title="Settings" subtitle="Konfigurasi perusahaan, jam kerja, lembur, geofence, dan hak akses role"
        testId="settings-header"
        actions={<Button onClick={save} data-testid="save-settings" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Save size={14} className="mr-1" /> Simpan</Button>}
      />
      <Section>
        {/* Company */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// PERUSAHAAN</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Nama</Label>
              <Input value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Alamat</Label>
              <Input value={s.company_address || ""} onChange={(e) => set("company_address", e.target.value)} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
          </div>
        </div>

        {/* Jam kerja */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// JAM KERJA & KETERLAMBATAN</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Mulai</Label>
              <Input type="time" value={s.work_start || ""} onChange={(e) => set("work_start", e.target.value)} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Selesai</Label>
              <Input type="time" value={s.work_end || ""} onChange={(e) => set("work_end", e.target.value)} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Toleransi Telat (menit)</Label>
              <Input type="number" value={s.late_threshold_minutes ?? ""} onChange={(e) => set("late_threshold_minutes", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Potongan/Menit Telat (Rp)</Label>
              <Input type="number" value={s.late_deduction_per_minute ?? ""} onChange={(e) => set("late_deduction_per_minute", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
          </div>
          <div className="mt-4">
            <Label className="text-xs font-mono uppercase text-neutral-400">Hari Kerja</Label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {DAYS.map((d, i) => (
                <button key={i} type="button" onClick={() => toggleDay(i)}
                  data-testid={`day-${i}`}
                  className={`px-3 py-1 border font-mono text-xs uppercase ${
                    (s.work_days || []).includes(i) ? "bg-[#FF6600] text-black border-[#FF6600]" : "bg-neutral-900 border-neutral-800 text-neutral-400"
                  }`}>{d}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Geofence */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// GEOFENCE ABSENSI</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Radius Default (meter)</Label>
              <Input type="number" value={s.default_radius_m ?? ""} onChange={(e) => set("default_radius_m", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
              <div className="text-[11px] text-neutral-500 mt-1">Absen ditolak bila lokasi di luar radius site.</div>
            </div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Ukuran Foto Minimum (byte)</Label>
              <Input type="number" value={s.min_photo_bytes ?? ""} onChange={(e) => set("min_photo_bytes", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
              <div className="text-[11px] text-neutral-500 mt-1">Foto lebih kecil dari ini akan ditolak.</div>
            </div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Ambang Ketajaman Foto (Laplacian)</Label>
              <Input type="number" step="1" value={s.blur_threshold ?? ""} onChange={(e) => set("blur_threshold", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="blur-threshold" />
              <div className="text-[11px] text-neutral-500 mt-1">Foto absen dengan skor ketajaman di bawah nilai ini akan ditolak otomatis. Default 60. Naikkan bila terlalu banyak lolos, turunkan bila terlalu ketat.</div>
            </div>
            <div><Label className="text-xs font-mono uppercase text-neutral-400">Zona Waktu (offset jam dari UTC)</Label>
              <Input type="number" step="1" value={s.timezone_offset_hours ?? ""} onChange={(e) => set("timezone_offset_hours", num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="tz-offset" />
              <div className="text-[11px] text-neutral-500 mt-1">WIB=7, WITA=8, WIT=9. Dipakai untuk deteksi terlambat &amp; validasi jam shift.</div>
            </div>
          </div>
        </div>

        {/* Overtime */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// LEMBUR (MULTIPLIER × GAJI/JAM)</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {[
              ["ot_multiplier_weekday", "Hari Biasa"],
              ["ot_multiplier_weekend", "Sabtu/Minggu"],
              ["ot_multiplier_national_holiday", "Libur Nasional"],
              ["ot_multiplier_religious_holiday", "Hari Raya"],
              ["ot_multiplier_custom", "Custom"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs font-mono uppercase text-neutral-400">{l}</Label>
                <Input type="number" step="0.1" value={s[k] ?? ""} onChange={(e) => set(k, num(e.target.value))} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid={`ot-${k}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Salary default */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// GAJI</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-400">Tipe Gaji Default</Label>
              <Select value={s.salary_type_default || "monthly"} onValueChange={(v) => set("salary_type_default", v)}>
                <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                  <SelectItem value="monthly">Bulanan</SelectItem>
                  <SelectItem value="daily">Harian</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Permissions matrix */}
        <div className="border border-neutral-800 bg-[#141414] p-5 mb-4">
          <div className="font-mono text-[10px] uppercase text-[#FF6600] mb-3">// HAK AKSES ROLE PER MODUL</div>
          <div className="text-xs text-neutral-500 mb-3">Centang modul yang boleh diakses oleh tiap role. Admin selalu punya akses penuh (*).</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 border border-neutral-800">Modul</th>
                  {ROLES.map((r) => <th key={r} className="p-2 border border-neutral-800 uppercase text-[#FF6600]">{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {MODULES.map((m) => (
                  <tr key={m}>
                    <td className="p-2 border border-neutral-800">{m}</td>
                    {ROLES.map((r) => {
                      const allowed = r === "admin" || (s.role_permissions?.[r] || []).includes(m) || (s.role_permissions?.[r] || []).includes("*");
                      return (
                        <td key={r} className="p-2 border border-neutral-800 text-center">
                          <input type="checkbox" checked={allowed} disabled={r === "admin"}
                            onChange={() => togglePerm(r, m)}
                            data-testid={`perm-${r}-${m}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
}
